import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlanQueueFilter, PlanSortDir, PlanSortKey } from '@/features/plan/lib/movements';
import type { PoolId } from '@/features/plan/lib/planResources';

interface PlanViewState {
  /** Whether the driver-timeline Gantt is shown. Defaults to visible. */
  ganttVisible: boolean;
  /** The resource strip folded down to its header bar. Defaults to open. */
  resourcesCollapsed: boolean;
  /** Active resource pool — drives both the strip tabs and the Gantt rows. */
  resourcePool: PoolId;
  /** Selected depot (an organisation site / location id) — null = all depots. */
  depotId: string | null;
  /** Additional days beyond today included in the horizon (0 = today only). */
  horizonDays: number;
  /** Active Job Queue filter chip. */
  queueFilter: PlanQueueFilter;
  /** Cargo-type filter ('All' = no filter). */
  cargoFilter: string;
  /** Country-flow filter, e.g. 'IE → GB' ('All' = no filter). */
  directionFilter: string;
  /** Free-text search over the Job Queue ('' = none). */
  search: string;
  /** Completed jobs in the Job Queue. Off by default. */
  showCompleted: boolean;
  /** The Job Queue's sort; `null` is the default, newest first. */
  sortKey: PlanSortKey | null;
  sortDir: PlanSortDir;
  setGanttVisible: (visible: boolean) => void;
  setResourcesCollapsed: (collapsed: boolean) => void;
  setResourcePool: (pool: PoolId) => void;
  setDepot: (id: string | null) => void;
  setHorizonDays: (days: number) => void;
  setQueueFilter: (f: PlanQueueFilter) => void;
  setCargoFilter: (c: string) => void;
  setDirectionFilter: (d: string) => void;
  setSearch: (search: string) => void;
  setShowCompleted: (show: boolean) => void;
  setSortKey: (key: PlanSortKey | null) => void;
  setSortDir: (dir: PlanSortDir) => void;
}

/**
 * Toolbar / view state for the Movement Assignment (Plan) cockpit — Gantt
 * visibility, depot, horizon and the Job Queue filter. Persisted (survives tab
 * switches and reloads) so an operator returns to the same view.
 */
export const usePlanViewStore = create<PlanViewState>()(
  persist(
    (set) => ({
      ganttVisible: true,
      resourcesCollapsed: false,
      resourcePool: 'driverRoster',
      depotId: null,
      horizonDays: 0,
      queueFilter: 'All',
      cargoFilter: 'All',
      directionFilter: 'All',
      search: '',
      showCompleted: false,
      sortKey: null,
      sortDir: 'asc',
      setGanttVisible: (visible) => set({ ganttVisible: visible }),
      setResourcesCollapsed: (collapsed) => set({ resourcesCollapsed: collapsed }),
      setResourcePool: (pool) => set({ resourcePool: pool }),
      setDepot: (id) => set({ depotId: id }),
      setHorizonDays: (days) => set({ horizonDays: days }),
      setQueueFilter: (f) => set({ queueFilter: f }),
      setCargoFilter: (c) => set({ cargoFilter: c }),
      setDirectionFilter: (d) => set({ directionFilter: d }),
      setSearch: (search) => set({ search }),
      setShowCompleted: (show) => set({ showCompleted: show }),
      setSortKey: (key) => set({ sortKey: key }),
      setSortDir: (dir) => set({ sortDir: dir }),
    }),
    { name: 'my:tms:planView' },
  ),
);
